/**
 * Line GeoJSON Helpers
 *
 * Utility functions for converting Line geometry to GeoJSON format for MapLibre.
 * Part of multi-geometry support (ETAP 4).
 *
 * @module components/map/GeometryRenderer/LineRenderer/utils/lineGeoJsonHelpers
 */

import { LineType } from '../../../../../types/geometry';
import { interpolateLinePoints, catmullRomSegment } from './lineInterpolation';
import { CATMULL_ROM_SEGMENTS } from '../../../../../constants';
import type { TaskGeometry, Coordinates } from '../../../../../types';
import type { Feature, LineString, FeatureCollection } from 'geojson';

/**
 * Get segment with Catmull-Rom context (4 points: p0, p1, p2, p3)
 *
 * For proper Catmull-Rom interpolation of segment `p1 → p2`, we need 4 points:
 * - p0 (grandparent): parent of p1, or duplicate p1 if p1 is root
 * - p1 (parent): start of segment
 * - p2 (child): end of segment
 * - p3 (grandchild): any child of p2, or duplicate p2 if p2 is leaf
 *
 * @param parentIdx - Index of parent point (start of segment)
 * @param childIdx - Index of child point (end of segment)
 * @param linePoints - Array of all line points
 * @param parentIndices - Array of parent indices (parent-child relationships)
 * @returns Array of 4 points for Catmull-Rom interpolation: [p0, p1, p2, p3]
 *
 * @example
 * ```typescript
 * // Line: 1→2→3→4
 * // parentIndices: [null, 0, 1, 2]
 * // Segment 2→3 (parentIdx=1, childIdx=2):
 * getSegmentWithContext(1, 2, linePoints, parentIndices);
 * // → [p1, p2, p3, p4] (grandparent=1, parent=2, child=3, grandchild=4)
 * ```
 *
 * @internal Exported for use in LineSegmentHandles (midpoint calculation)
 */
export function getSegmentWithContext(
  parentIdx: number,
  childIdx: number,
  linePoints: Coordinates[],
  parentIndices: (number | null)[]
): Coordinates[] {
  const p1 = linePoints[parentIdx]; // Parent (start of segment)
  const p2 = linePoints[childIdx]; // Child (end of segment)

  if (!p1 || !p2) {
    // Safety fallback: return just 2 points if data is invalid
    return [p1, p2].filter(Boolean) as Coordinates[];
  }

  // p0: Grandparent (parent of p1)
  let p0: Coordinates;
  const grandparentIdx = parentIndices[parentIdx];
  if (
    grandparentIdx !== null &&
    grandparentIdx !== undefined &&
    linePoints[grandparentIdx]
  ) {
    p0 = linePoints[grandparentIdx];
  } else {
    // p1 is root → duplicate p1 for Catmull-Rom edge case
    p0 = p1;
  }

  // p3: Grandchild (any child of p2)
  let p3: Coordinates;
  const grandchildIdx = parentIndices.findIndex(
    (parent, _idx) => parent === childIdx
  );
  if (grandchildIdx !== -1 && linePoints[grandchildIdx]) {
    p3 = linePoints[grandchildIdx];
  } else {
    // p2 is leaf → duplicate p2 for Catmull-Rom edge case
    p3 = p2;
  }

  return [p0, p1, p2, p3];
}

/**
 * Interpolate single segment with context (for branching lines)
 *
 * Applies Catmull-Rom interpolation to single segment p1→p2 using context points p0 and p3.
 * This enables rounded lines in branching mode without duplicating segments.
 *
 * @param p0 - Grandparent (context before p1)
 * @param p1 - Parent (start of segment)
 * @param p2 - Child (end of segment)
 * @param p3 - Grandchild (context after p2)
 * @param lineType - Line type (Straight or Curved)
 * @returns Interpolated points for segment p1→p2
 *
 * @example Straight line
 * ```typescript
 * interpolateSingleSegment(p0, p1, p2, p3, LineType.Straight);
 * // → [p1, p2] (no interpolation)
 * ```
 *
 * @example Curved line
 * ```typescript
 * interpolateSingleSegment(p0, p1, p2, p3, LineType.Curved);
 * // → [p1, ...interpolated points..., p2] (Catmull-Rom curve)
 * ```
 */
function interpolateSingleSegment(
  p0: Coordinates,
  p1: Coordinates,
  p2: Coordinates,
  p3: Coordinates,
  lineType: LineType
): Coordinates[] {
  // Straight line: just return the two endpoints
  if (lineType === LineType.Straight) {
    return [p1, p2];
  }

  // Curved line: apply Catmull-Rom interpolation to this segment
  return catmullRomSegment(p0, p1, p2, p3, CATMULL_ROM_SEGMENTS);
}

/**
 * Create GeoJSON from Line geometry with branching support
 *
 * Converts line points to GeoJSON format compatible with MapLibre GL JS.
 * Coordinates are in [longitude, latitude] format (GeoJSON standard).
 *
 * Branching Support (v3.1.0):
 * - If parentIndices exist → Creates FeatureCollection with separate segments for each edge
 * - If no parentIndices → Creates simple LineString (backward compatible)
 *
 * @param params - Object containing geometry
 * @param params.geometry - Line geometry with linePoints and optional parentIndices
 * @returns GeoJSON FeatureCollection with LineString features
 *
 * @example
 * ```typescript
 * // Simple line (backward compat)
 * const geometry = {
 *   linePoints: [p1, p2, p3],
 *   lineColor: '#ff0000',
 * };
 * // Returns FeatureCollection with single LineString: p1→p2→p3
 *
 * // Branched line
 * const geometry = {
 *   linePoints: [p1, p2, p3, p1, p5],
 *   parentIndices: [null, 0, 1, 2, 1], // p1→p2→p3→p1 + p2→p5 (branch)
 * };
 * // Returns FeatureCollection with segments: [p1→p2, p2→p3, p3→p1, p2→p5]
 * ```
 */
export function createLineGeoJson({
  geometry,
}: {
  geometry: TaskGeometry;
}): FeatureCollection<LineString> {
  const linePoints = geometry.linePoints || [];
  const parentIndices = geometry.parentIndices || [];
  const lineType = geometry.lineType || LineType.Straight;

  // CASE 1: No parentIndices → Backward compatible (sequential line without branching support)
  if (parentIndices.length === 0) {
    // Apply interpolation based on line type (Straight/Bezier/Arc)
    const interpolatedPoints = interpolateLinePoints(linePoints, lineType);

    // Convert [lat, lng] to [lng, lat] (GeoJSON standard)
    const coordinates = interpolatedPoints.map(
      (point: { lng: number; lat: number }) => [point.lng, point.lat]
    );

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates,
          },
          properties: {
            geometryId: geometry.id,
            color: geometry.lineColor,
            width: geometry.lineWidth,
            lineType,
          },
        },
      ],
    };
  }

  // CASE 2: Has parentIndices → Segment-based rendering (supports branching)
  // Use Catmull-Rom context (4 points) for each segment to enable rounded lines with branches
  const segments: Feature<LineString>[] = [];

  linePoints.forEach((_point: Coordinates, index: number) => {
    const parentIdx = parentIndices[index];

    // Skip root point (no parent)
    if (parentIdx === null || parentIdx === undefined) return;

    // Safety: ensure parent index is valid
    if (parentIdx < 0 || parentIdx >= linePoints.length) return;

    // Safety: skip if point is its own parent (invalid state)
    // This can happen if activeParentIndex wasn't reset properly
    if (parentIdx === index) return;

    const parentPoint = linePoints[parentIdx];
    if (!parentPoint) return;

    // Get segment with Catmull-Rom context (p0, p1, p2, p3) for proper interpolation
    // This enables rounded lines even with branches (≥4 points for context)
    const segmentContext = getSegmentWithContext(
      parentIdx,
      index,
      linePoints,
      parentIndices
    );

    // Safety: ensure we have all 4 points for interpolation
    if (segmentContext.length < 2) return; // Need at least p1, p2

    const [p0, p1, p2, p3] = segmentContext;
    if (!p0 || !p1 || !p2 || !p3) return; // Ensure all points exist

    // Interpolate ONLY this segment (p1→p2) using context (p0, p3)
    const interpolatedSegment = interpolateSingleSegment(
      p0,
      p1,
      p2,
      p3,
      lineType
    );

    // Convert to GeoJSON coordinates
    const coordinates = interpolatedSegment.map(
      (p: { lng: number; lat: number }) => [p.lng, p.lat]
    );

    // Add segment feature
    segments.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates,
      },
      properties: {
        geometryId: geometry.id,
        color: geometry.lineColor,
        width: geometry.lineWidth,
        lineType,
        segmentIndex: index, // For debugging
        parentIndex: parentIdx, // For debugging
      },
    });
  });

  return {
    type: 'FeatureCollection',
    features: segments,
  };
}
